import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PublicFaqSection from "@/components/PublicFaqSection";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, DatabaseBackup, History, ShieldCheck } from "lucide-react";

const JotformBackupPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-grow">
        <section className="py-16 md:py-24 border-b">
          <div className="container mx-auto px-6 max-w-5xl">
            <p className="text-sm font-semibold text-primary mb-3">Jotform backup software</p>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground max-w-3xl">
              Automated Jotform backups for form structure and settings
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-3xl leading-relaxed">
              FAB creates independent backups of your Jotform form metadata, field definitions,
              properties, webhooks, and configuration so important forms are easier to inspect,
              export, and restore after accidental changes.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg">
                <Link to="/pricing">View Pricing</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/help/what-fab-backs-up-for-jotform">What FAB Backs Up</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="container mx-auto px-6 grid gap-6 md:grid-cols-3">
            {[
              {
                icon: <DatabaseBackup className="h-8 w-8 text-primary" />,
                title: "Daily automated backups",
                text: "Connected Jotform accounts get scheduled backups so form structure changes are captured without manual exports.",
              },
              {
                icon: <History className="h-8 w-8 text-primary" />,
                title: "Backup history",
                text: "Review past backups, compare timestamps, and download stored backup JSON when you need a portable record.",
              },
              {
                icon: <ShieldCheck className="h-8 w-8 text-primary" />,
                title: "Independent storage",
                text: "FAB keeps backup records outside Jotform, giving teams a separate safety net for critical form configuration.",
              },
            ].map((item) => (
              <Card key={item.title}>
                <CardHeader>
                  {item.icon}
                  <CardTitle className="text-xl">{item.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-muted-foreground">{item.text}</CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-6 max-w-4xl">
            <h2 className="text-3xl font-bold text-foreground mb-6">What the backup is for</h2>
            <div className="grid gap-4">
              {[
                "Recovering from accidental form deletion or unwanted edits.",
                "Keeping a versioned record before large form changes.",
                "Exporting form structure for audits, migration planning, or internal review.",
                "Reducing reliance on a single form platform as the only source of truth.",
              ].map((item) => (
                <div key={item} className="flex gap-3 text-muted-foreground">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <PublicFaqSection />
      </main>
      <Footer />
    </div>
  );
};

export default JotformBackupPage;
